import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, Cog, LogOut, ShieldCheck, Crown, User as UserIcon } from 'lucide-react';
import { hasAccess } from './LockedGate';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const { t, lang, toggleLang, isRTL } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const light = true;
  const showSubCta = !hasAccess(user);
  const L = (ar, en) => tx(lang, ar, en);
  const act = (item, isActive) => isActive || (item.to === '/library' && ['/articles', '/videos'].some(p => location.pathname.startsWith(p)));

  const navItems = [
    { to: '/', label: t.nav.home },
    { to: '/courses', label: t.nav.courses },
    { to: '/library', label: t.nav.library },
    { to: '/simulations', label: t.nav.simulations },
    { to: '/subscription', label: t.nav.subscription },
    { to: '/about', label: t.nav.about },
    { to: '/contact', label: t.nav.contact },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${light ? (scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-sm' : 'bg-transparent') : (scrolled ? 'bg-[#0a1628]/95 backdrop-blur-md border-b border-blue-900/30' : 'bg-transparent')}`}>
      <div className="site-container">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Cog className="w-5 h-5 text-white" />
            </div>
            <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
              <div className={`${light ? 'text-slate-900' : 'text-white'} font-bold text-lg leading-tight`}>{t.footer.brand}</div>
              <div className={`${light ? 'text-blue-600' : 'text-blue-300'} text-[11px] leading-tight`}>{tx(lang, 'تعلم متقدمة من الصفر للاحتراف', 'Learn from zero to pro')}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `px-3 py-2 text-sm rounded-md font-medium transition-colors ${act(item, isActive) ? (light ? 'text-blue-600 font-bold' : 'text-blue-400') : (light ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white')}`}
              >
                {({ isActive }) => (
                  <span className="relative">
                    {item.label}
                    {act(item, isActive) && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-500 rounded-full" />}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button onClick={toggleLang} data-testid="nav-lang-toggle" className={`hidden md:flex items-center gap-1 px-3 py-2 rounded-md text-sm transition-colors ${light ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' : 'text-slate-300 hover:text-white hover:bg-blue-900/30'}`}>
              <Globe className="w-4 h-4" />
              <span>{tx(lang, 'EN', 'عربي')}</span>
            </button>

            {showSubCta && user && (
              <Link to="/subscription" data-testid="nav-subscribe-cta" className="btn-subscribe gap-1.5 text-xs sm:text-sm px-3 sm:px-4 h-9 sm:h-10">
                <Crown className="w-4 h-4" />{L('اشترك الآن', 'Subscribe')}
              </Link>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className={`hidden sm:flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-colors ${light ? 'border-slate-200 bg-white hover:bg-slate-50' : 'border-blue-500/40 hover:bg-blue-900/30'}`}>
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-blue-600/30 flex items-center justify-center"><UserIcon className="w-4 h-4 text-blue-300" /></div>
                    )}
                    <span className={`${light ? 'text-slate-700' : 'text-slate-200'} text-sm max-w-[120px] truncate`}>{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="bg-white border-slate-200 text-slate-700 w-52">
                  <DropdownMenuLabel className="text-slate-400 text-xs font-normal" dir="ltr">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-200" />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer focus:bg-slate-100 focus:text-slate-900" data-testid="profile-menu-item">
                    <UserIcon className="w-4 h-4 me-2 text-blue-400" />
                    {tx(lang, 'حسابي', 'My Account')}
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">
                      <ShieldCheck className="w-4 h-4 me-2 text-blue-400" />
                      {t.auth.adminBadge}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer focus:bg-red-50 focus:text-red-700 text-red-600">
                    <LogOut className="w-4 h-4 me-2" />
                    {tx(lang, 'تسجيل الخروج', 'Logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline-flex" data-testid="nav-login-btn">
                  <Button variant="outline" className={light ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900' : 'border-blue-500/40 text-slate-200 hover:bg-blue-900/30 hover:text-white'}>
                    {t.nav.login}
                  </Button>
                </Link>
                <Link to="/subscription" className="btn-subscribe gap-1.5 h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm" data-testid="nav-register-btn">
                  <Crown className="w-4 h-4" />{t.nav.subscribe}
                </Link>
              </>
            )}

            <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className={`lg:hidden p-2 ${light ? 'text-slate-800' : 'text-slate-200'}`}>
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className={`lg:hidden border-t ${light ? 'bg-white border-slate-200 shadow-lg' : 'bg-[#0a1628] border-blue-900/30'}`}>
          <nav className="flex flex-col py-4 px-4 gap-1">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `px-3 py-2 rounded-md text-sm ${act(item, isActive) ? (light ? 'bg-blue-50 text-blue-700 font-bold' : 'bg-blue-600/20 text-blue-300') : (light ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-300 hover:bg-blue-900/30')}`}
              >
                {item.label}
              </NavLink>
            ))}
            {user && (
              <NavLink to="/profile" data-testid="mobile-profile-link" className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 ${light ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-300 hover:bg-blue-900/30'}`}>
                <UserIcon className="w-4 h-4" />{L('حسابي', 'My Account')}
              </NavLink>
            )}
            {user?.role === 'admin' && (
              <NavLink to="/admin" data-testid="mobile-admin-link" className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 ${light ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-300 hover:bg-blue-900/30'}`}>
                <ShieldCheck className="w-4 h-4" />{t.auth.adminBadge}
              </NavLink>
            )}
            <div className={`flex gap-2 pt-3 border-t mt-2 ${light ? 'border-slate-200' : 'border-blue-900/30'}`}>
              <button data-testid="mobile-lang-toggle" onClick={toggleLang} className={`flex-1 px-3 py-2 rounded-md border flex items-center justify-center gap-1 text-sm ${light ? 'text-slate-700 border-slate-300 hover:bg-slate-50' : 'text-slate-300 border-blue-900/40'}`}>
                <Globe className="w-4 h-4" /> {tx(lang, 'English', 'عربي')}
              </button>
              {user ? (
                <Button data-testid="mobile-logout-btn" onClick={handleLogout} variant="outline" className={`flex-1 border-red-500/40 text-red-500 ${light ? 'bg-white hover:bg-red-50' : 'bg-transparent'}`}>
                  <LogOut className="w-4 h-4 me-2" />{tx(lang, 'خروج', 'Logout')}
                </Button>
              ) : (
                <>
                  <Link to="/login" className="flex-1"><Button data-testid="mobile-login-btn" variant="outline" className={`w-full ${light ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50' : 'border-blue-500/40 text-slate-200 bg-transparent'}`}>{t.nav.login}</Button></Link>
                  <Link to="/subscription" className="flex-1 btn-subscribe h-10 text-sm" data-testid="mobile-nav-register-btn"><Crown className="w-4 h-4" />{t.nav.subscribe}</Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
